import { Link } from 'react-router-dom';
import { formatPhoneDisplay, formatShortDate, statusLabel } from '../../utils/whatsapp';

/** "walk_in_lead" -> "Walk in lead". */
const humanizeSource = (source) => {
  const text = String(source || '')
    .replace(/_/g, ' ')
    .trim();
  return text ? text[0].toUpperCase() + text.slice(1) : '';
};

/** "1234.5" + "INR" -> "₹1,234.50"; an unknown currency code falls back to "CODE 1,234.50". */
const formatMoney = (amount, currency) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(n);
  } catch {
    return `${currency || ''} ${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`.trim();
  }
};

const humanize = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .trim();

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-2">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="text-right">{children}</dd>
  </div>
);

/**
 * The right-hand contact panel: who this number belongs to, lead details, notes, a link that opens
 * the same conversation in WhatsApp itself, and an opted-out badge.
 * @param {{contact: any, phone: string, suppressed: boolean, onClose?: () => void}} props
 */
const ContactPanel = ({ contact, phone, suppressed, onClose }) => {
  const digits = String(phone || '').replace(/\D/g, '');
  const lead = contact?.lead;
  const customer = contact?.customer;
  const atelier = contact?.atelier;
  const orders = customer?.orders;
  const recent = (orders?.recent || []).slice(0, 3);
  const totals = (orders?.totals || []).filter((t) => t && t.currency);

  return (
    <div className="flex w-full flex-col overflow-y-auto bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-medium text-foreground">Contact info</h3>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close contact info"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        )}
      </div>
      <div className="space-y-4 px-4 py-4 text-sm">
        <div>
          <p className="font-medium text-foreground">{contact?.name || formatPhoneDisplay(phone)}</p>
          <p className="text-muted-foreground">{formatPhoneDisplay(phone)}</p>
          {suppressed && (
            <span className="mt-2 inline-block rounded-md bg-red-50 dark:bg-red-950/50 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-300">
              Opted out
            </span>
          )}
        </div>

        {contact?.source && (
          <div>
            <p className="text-xs uppercase text-muted-foreground/70">Source</p>
            <p className="text-foreground/80">{humanizeSource(contact.source)}</p>
          </div>
        )}

        {lead && (
          <div>
            <p className="text-xs uppercase text-muted-foreground/70">Lead details</p>
            <dl className="mt-1 space-y-1 text-foreground/80">
              {lead.event && (
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Event</dt>
                  <dd>{lead.event}</dd>
                </div>
              )}
              {lead.style && (
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Style</dt>
                  <dd>{lead.style}</dd>
                </div>
              )}
              {lead.size_tried && (
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Size tried</dt>
                  <dd>{lead.size_tried}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {customer && (
          <div>
            <p className="text-xs uppercase text-muted-foreground/70">Customer</p>
            <dl className="mt-1 space-y-1 text-foreground/80">
              {customer.name && <Row label="Name">{customer.name}</Row>}
              {customer.email && (
                <Row label="Email">
                  <span className="break-all">{customer.email}</span>
                </Row>
              )}
              {customer.default_size && <Row label="Default size">{customer.default_size}</Row>}
              {customer.preferred_currency && <Row label="Currency">{customer.preferred_currency}</Row>}
              {customer.member_since && formatShortDate(customer.member_since) && (
                <Row label="Member since">{formatShortDate(customer.member_since)}</Row>
              )}
              {orders && typeof orders.count === 'number' && <Row label="Orders">{orders.count}</Row>}
              {totals.map((t) => (
                <Row key={t.currency} label={`Paid total (${t.currency})`}>
                  {formatMoney(t.total_spent, t.currency)}
                </Row>
              ))}
            </dl>
            {recent.length > 0 && (
              <ul aria-label="Recent orders" className="mt-2 space-y-1 text-foreground/80">
                {recent.map((o) => (
                  <li key={o.id} className="flex justify-between gap-2 rounded-lg bg-muted/50 px-2 py-1">
                    <span>
                      <span className="font-medium">#{o.order_number}</span>
                      <span className="block text-xs text-muted-foreground">
                        {[statusLabel(o.status), o.created_at && formatShortDate(o.created_at)]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      {formatMoney(o.total, o.currency)}
                      {o.payment_status && (
                        <span
                          data-testid="payment-chip"
                          className="mt-0.5 block rounded-md bg-accent px-1.5 text-[11px] capitalize text-foreground/80"
                        >
                          {humanize(o.payment_status)}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {customer.admin_path && (
              <Link to={customer.admin_path} className="mt-2 inline-block text-sm text-[#008069] underline">
                View customer
              </Link>
            )}
          </div>
        )}

        {atelier && (
          <div>
            <p className="text-xs uppercase text-muted-foreground/70">Atelier</p>
            <dl className="mt-1 space-y-1 text-foreground/80">
              {atelier.company_name && <Row label="Company">{atelier.company_name}</Row>}
              {atelier.kind && (
                <Row label="Kind">
                  <span className="capitalize">{humanize(atelier.kind)}</span>
                </Row>
              )}
              {atelier.contact_person && <Row label="Contact">{atelier.contact_person}</Row>}
              {atelier.status && (
                <Row label="Status">
                  <span className="capitalize">{humanize(atelier.status)}</span>
                </Row>
              )}
            </dl>
          </div>
        )}

        {contact?.notes && (
          <div>
            <p className="text-xs uppercase text-muted-foreground/70">Notes</p>
            <p className="whitespace-pre-wrap text-foreground/80">{contact.notes}</p>
          </div>
        )}

        {digits && (
          <a
            href={`https://wa.me/${digits}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-sm text-[#008069] underline"
          >
            Open in WhatsApp
          </a>
        )}
      </div>
    </div>
  );
};

export default ContactPanel;
